/**
 * WebMCP (Web Model Context Protocol) Integration
 * Exposes browser-native callable tools to autonomous AI agents via document.modelContext / navigator.modelContext.
 * Complies with the official WebMCP & Agentic Resource Discovery (ARD) specifications.
 */

import { HOSTING_PLANS } from '../data/siteData';

export function initializeWebMCP(): void {
  if (typeof window === 'undefined') return;

  // Check for WebMCP support (document.modelContext or navigator.modelContext)
  const modelContext = (window.document as any).modelContext || (window.navigator as any).modelContext;

  const tools = [
    {
      name: 'calculateWebsiteQuote',
      description: 'Calculate instant pricing (KES), project duration, and M-Pesa Lipa Pole Pole milestone terms for website packages, mobile apps, or custom ERP software in Kenya.',
      inputSchema: {
        type: 'object',
        properties: {
          serviceType: {
            type: 'string',
            enum: ['landing-page', 'starter-website', 'business-website', 'business-pro', 'ecommerce', 'custom-erp', 'mobile-app'],
            description: 'The digital infrastructure vertical requested',
          },
          includeDomainAndHosting: {
            type: 'boolean',
            description: 'Whether to bundle free 1-year .co.ke domain registration and NVMe LiteSpeed cPanel cloud hosting',
          },
        },
        required: ['serviceType'],
      },
      execute: async (args: { serviceType: string; includeDomainAndHosting?: boolean }) => {
        const rates: Record<string, { priceKES: number; depositKES: number; turnaround: string; label: string }> = {
          'landing-page': { priceKES: 9000, depositKES: 3000, turnaround: '48 to 72 Hours', label: 'High-Converting Landing Page' },
          'starter-website': { priceKES: 15000, depositKES: 5000, turnaround: '3 to 4 Days', label: 'Starter Business Website' },
          'business-website': { priceKES: 25000, depositKES: 8000, turnaround: '5 to 7 Days', label: 'Corporate Business Website' },
          'business-pro': { priceKES: 30000, depositKES: 10000, turnaround: '7 to 10 Days', label: 'Business Pro Portal & CRM' },
          'ecommerce': { priceKES: 35000, depositKES: 12000, turnaround: '10 to 14 Days', label: 'E-Commerce Store with Safaricom Daraja M-Pesa Checkout' },
          'custom-erp': { priceKES: 35000, depositKES: 12000, turnaround: '14 to 21 Days', label: 'Custom Relational ERP & KRA eTIMS Middleware' },
          'mobile-app': { priceKES: 60000, depositKES: 20000, turnaround: '21 to 30 Days', label: 'Cross-Platform iOS & Android Mobile Application' },
        };

        const item = rates[args.serviceType] || rates['starter-website'];
        return {
          service: item.label,
          totalPriceKES: item.priceKES,
          lipaPolePoleDepositKES: item.depositKES,
          turnaround: item.turnaround,
          currency: 'KES',
          includedDeliverables: [
            'Mobile & WhatsApp Conversion Optimization',
            'Safaricom 4G Speed Benchmarked (Sub-0.8s LCP)',
            'Free SSL Certificate & Security Hardening',
            'Google Maps Local 3-Pack Schema Graph',
            args.includeDomainAndHosting !== false ? '1 Year Free .co.ke Domain & NVMe LiteSpeed Cloud Hosting' : null,
          ].filter(Boolean),
          nextStep: 'Book a 45-minute working strategy session or launch directly via M-Pesa Paybill 542542, A/C 01550.',
          contactHotline: '+254 114 646 341',
        };
      },
    },
    {
      name: 'getPricingCatalog',
      description: 'Retrieve the verified official 2026 pricing catalog and service packages for Thinkers Arcade in Kenya.',
      inputSchema: {
        type: 'object',
        properties: {},
      },
      execute: async () => {
        return {
          catalogName: 'Thinkers Arcade Official 2026 Price List',
          currency: 'KES',
          paymentMethods: ['Safaricom Lipa na M-Pesa Paybill (542542, A/C 01550)', 'Direct Bank EFT/RTGS', 'Milestone-based Lipa Pole Pole'],
          catalogUrl: 'https://thinkersarcade.co.ke/offer-catalog.json',
          services: [
            { category: 'Websites', startingKES: 9000, maxKES: 35000, delivery: '48h to 7 days' },
            { category: 'Mobile Apps', startingKES: 60000, maxKES: 120000, delivery: '3 to 6 weeks' },
            { category: 'Custom ERPs', startingKES: 35000, maxKES: 150000, delivery: '2 to 4 weeks' },
            { category: 'Cloud NVMe Hosting', startingKES: 6032, maxKES: 20000, billing: 'Annual' },
          ],
        };
      },
    },
    {
      name: 'getHostingPlans',
      description: 'Retrieve high-speed PCIe Gen4 NVMe LiteSpeed cloud hosting specifications and pricing in Kenya.',
      inputSchema: {
        type: 'object',
        properties: {},
      },
      execute: async () => {
        return {
          datacenter: 'Kenya Internet Exchange Point (KIXP) Peered / Global Cloud CDN',
          webServer: 'LiteSpeed Enterprise with LSCache',
          storage: 'Enterprise PCIe Gen4 NVMe SSD RAID-10',
          plans: HOSTING_PLANS.map((p) => ({
            id: p.id,
            name: p.name,
            targetAudience: p.targetAudience,
            priceYearKES: p.priceYear,
            storageDisk: p.specs.disk,
            ram: p.specs.ram,
            bandwidth: p.specs.bandwidth,
            domains: p.specs.domain,
            emails: p.specs.email,
          })),
        };
      },
    },
  ];

  // Expose on window for in-browser agents and inspectability
  (window as any).__webmcp_tools = tools;

  if (modelContext && typeof modelContext.registerTool === 'function') {
    tools.forEach((tool) => {
      try {
        modelContext.registerTool({
          name: tool.name,
          description: tool.description,
          inputSchema: tool.inputSchema,
          execute: tool.execute,
        });
      } catch (err) {
        // Continue registering remaining tools if one fails
      }
    });
  }
}
